import { z } from 'zod';
import { VoteType } from '../minutes/index.js';
import { nullablePositiveIdSchema, positiveIdSchema, withAtLeastOneField, } from '../../shared/index.js';
export const LiveMeetingDisplayMode = {
    agenda_item: 'agenda_item',
    blank: 'blank',
    custom_message: 'custom_message',
    motion: 'motion',
    presentation: 'presentation',
    speaker_timer: 'speaker_timer',
    vote_results: 'vote_results',
};
export const MeetingPhase = {
    closed: 'closed',
    in_progress: 'in_progress',
    not_started: 'not_started',
    recess: 'recess',
};
export const SpeakerStatus = {
    completed: 'completed',
    queued: 'queued',
    speaking: 'speaking',
};
export const LiveMeetingEventType = {
    consent_item_pulled: 'consent_item_pulled',
    item_activated: 'item_activated',
    motion_created: 'motion_created',
    roll_call_completed: 'roll_call_completed',
    roll_call_initiated: 'roll_call_initiated',
    speaker_finished: 'speaker_finished',
    speaker_started: 'speaker_started',
    speaker_time_added: 'speaker_time_added',
    vote_cancelled: 'vote_cancelled',
    vote_cast: 'vote_cast',
    vote_closed: 'vote_closed',
    vote_opened: 'vote_opened',
};
const liveMeetingDisplayModeSchema = z.enum(LiveMeetingDisplayMode);
const meetingPhaseSchema = z.enum(MeetingPhase);
const speakerStatusSchema = z.enum(SpeakerStatus);
const voteTypeSchema = z.enum(VoteType);
const nonNegativeIntegerSchema = z.number().int().nonnegative();
const optionalTimestampSecondsSchema = z.number().nonnegative().finite();
export const displayPayloadSchemaByMode = {
    agenda_item: z.object({
        itemTitle: z.string().min(1, 'Item title is required'),
        itemNumber: z.string().min(1).optional(),
        sectionTitle: z.string().min(1).optional(),
    }),
    blank: z.object({}),
    custom_message: z.object({
        body: z.string().min(1, 'Message body is required'),
        heading: z.string().min(1).optional(),
    }),
    motion: z.object({
        actionName: z.string().min(1, 'Action name is required'),
        motionText: z.string().min(1, 'Motion text is required'),
        moverName: z.string().min(1, 'Mover name is required'),
        seconderName: z.string().min(1).optional(),
        status: z.enum(['failed', 'passed', 'pending']),
    }),
    presentation: z.object({
        attachmentId: positiveIdSchema,
        currentPage: z.number().int().positive(),
        pageCount: z.number().int().positive(),
        title: z.string().min(1, 'Presentation title is required'),
    }),
    speaker_timer: z.object({
        // Absolute expiry of a running timer (ISO) — clients derive remaining = expiresAt − now so a
        // late-loading display is accurate. Null when paused/not running; use timeRemainingSeconds then.
        expiresAt: z.iso.datetime().nullable().optional(),
        // Whether the public display uses the centered fullsize layout. Seeded from the government's
        // `speakerTimer.fullsize` default on start, then toggled live by the clerk from the console.
        fullsize: z.boolean().default(false),
        isPaused: z.boolean().default(false),
        speakerName: z.string().min(1, 'Speaker name is required'),
        timeLimitSeconds: nonNegativeIntegerSchema,
        timeRemainingSeconds: nonNegativeIntegerSchema,
        topic: z.string().min(1).optional(),
    }),
    // The public display resolves the full motion and per-member tally from `motionId`, so the
    // payload only records which just-closed motion's results to show and until when. Once
    // `expiresAt` passes the display falls back to the active entry, even though `displayMode`
    // lingers as `vote_results` until the clerk moves the floor on.
    vote_results: z.object({
        motionId: positiveIdSchema,
        expiresAt: z.iso.datetime(),
    }),
};
export const liveMeetingDisplayStateSchema = z
    .discriminatedUnion('displayMode', [
    z.object({
        displayMode: z.literal(LiveMeetingDisplayMode.agenda_item),
        displayPayload: displayPayloadSchemaByMode.agenda_item,
    }),
    z.object({
        displayMode: z.literal(LiveMeetingDisplayMode.blank),
        displayPayload: displayPayloadSchemaByMode.blank,
    }),
    z.object({
        displayMode: z.literal(LiveMeetingDisplayMode.custom_message),
        displayPayload: displayPayloadSchemaByMode.custom_message,
    }),
    z.object({
        displayMode: z.literal(LiveMeetingDisplayMode.motion),
        displayPayload: displayPayloadSchemaByMode.motion,
    }),
    z.object({
        displayMode: z.literal(LiveMeetingDisplayMode.presentation),
        displayPayload: displayPayloadSchemaByMode.presentation,
    }),
    z.object({
        displayMode: z.literal(LiveMeetingDisplayMode.speaker_timer),
        displayPayload: displayPayloadSchemaByMode.speaker_timer,
    }),
    z.object({
        displayMode: z.literal(LiveMeetingDisplayMode.vote_results),
        displayPayload: displayPayloadSchemaByMode.vote_results,
    }),
])
    .describe('Discriminated union for live display state. displayPayload shape must match the selected displayMode.');
export const liveMeetingStateCreateSchema = z
    .object({
    activeMinutesEntryId: nullablePositiveIdSchema.optional(),
    activatedAt: z.iso.datetime().nullable().optional(),
    display: liveMeetingDisplayStateSchema,
    displayTemplateId: nullablePositiveIdSchema.optional(),
    displayToken: z.string().min(1).nullable().optional(),
    displayTokenExpiresAt: z.iso.datetime().nullable().optional(),
    meetingPhase: meetingPhaseSchema.default(MeetingPhase.not_started),
})
    .describe('Creates a live meeting state snapshot.');
export const liveMeetingStateUpdateSchema = withAtLeastOneField(z.object({
    activeMinutesEntryId: nullablePositiveIdSchema.optional(),
    activatedAt: z.iso.datetime().nullable().optional(),
    display: liveMeetingDisplayStateSchema.optional(),
    displayTemplateId: nullablePositiveIdSchema.optional(),
    displayToken: z.string().min(1).nullable().optional(),
    displayTokenExpiresAt: z.iso.datetime().nullable().optional(),
    meetingPhase: meetingPhaseSchema.optional(),
}), [
    'activeMinutesEntryId',
    'activatedAt',
    'display',
    'displayTemplateId',
    'displayToken',
    'displayTokenExpiresAt',
    'meetingPhase',
]).describe('Updates live meeting state fields; requires at least one mutable field.');
export const speakerCreateSchema = z.object({
    meetingMemberId: nullablePositiveIdSchema.optional(),
    minutesEntryId: nullablePositiveIdSchema.optional(),
    name: z.string().min(1, 'Speaker name is required'),
    orderById: z.number().int().min(1),
    status: speakerStatusSchema.default(SpeakerStatus.queued),
});
export const speakerUpdateSchema = withAtLeastOneField(z.object({
    meetingMemberId: nullablePositiveIdSchema.optional(),
    minutesEntryId: nullablePositiveIdSchema.optional(),
    name: z.string().min(1, 'Speaker name is required').optional(),
    orderById: z.number().int().min(1).optional(),
    status: speakerStatusSchema.optional(),
}), ['meetingMemberId', 'minutesEntryId', 'name', 'orderById', 'status']);
/**
 * Request body for adding a speaker to the queue from the clerk console. The name is optional —
 * an unnamed speaker can be queued and started (shown as "Speaker" on the display).
 * `minutesEntryId` lets the clerk queue the speaker against a specific agenda entry (the console
 * groups the queue by entry); omitted, the server links the speaker to the entry on the floor. The
 * server owns `status` and `orderById`.
 */
export const speakerQueueAddSchema = z
    .object({
    name: z.string().max(200).optional(),
    minutesEntryId: z.number().int().positive().optional(),
})
    .describe('Adds a speaker to the queue, optionally by name and against a specific agenda entry.');
/**
 * Request body for bringing a queued speaker on mic. The clerk picks a speaker-timer preset, so the
 * countdown length and both warning thresholds are always sent; they are baked into the live
 * meeting state's display payload so the public display knows exactly when to warn.
 */
export const speakerStartSchema = z
    .object({
    limitSeconds: z.number().int().positive().max(3600),
    firstThreshold: z.number().int().positive().max(3600),
    secondThreshold: z.number().int().positive().max(3600),
    // The fullsize layout preference the display starts with — seeded from the government's
    // `speakerTimer.fullsize` default by the console, then editable live.
    fullsize: z.boolean(),
})
    .refine((data) => data.firstThreshold > data.secondThreshold, {
    message: 'First warning threshold must be greater than second warning threshold',
    path: ['firstThreshold'],
})
    .describe('Starts a speaker with the chosen preset countdown length and warning thresholds.');
/**
 * Request body for editing a queued or on-mic speaker's name from the clerk console — used to fix a
 * misspelling, supply a name that wasn't known when the timer started, or clear a wrong name back to
 * blank (speaker names are optional, so an empty value returns the speaker to the unnamed state).
 * Renaming the on-mic speaker only patches the display name; it never touches the running countdown.
 */
export const speakerRenameSchema = z
    .object({
    name: z.string().trim().max(200),
})
    .describe('Renames a queued or on-mic speaker (blank clears the name).');
/** Request body for granting additional time to the active speaker. */
export const speakerAddTimeSchema = z
    .object({
    seconds: z.number().int().positive().max(3600),
})
    .describe('Adds time to the active speaker timer.');
/**
 * Request body for toggling the on-mic speaker's fullsize display layout from the clerk console,
 * updating only the `fullsize` flag on the live meeting state's speaker_timer payload.
 */
export const speakerSetFullsizeSchema = z
    .object({
    fullsize: z.boolean(),
})
    .describe('Toggles the fullsize layout of the active speaker timer display.');
export const livePresentationCreateSchema = z.object({
    attachmentId: positiveIdSchema,
    currentPage: z.number().int().positive().default(1),
    isActive: z.boolean().default(false),
    minutesEntryId: nullablePositiveIdSchema.optional(),
    orderById: z.number().int().min(1),
    pageCount: z.number().int().positive().nullable().optional(),
    title: z.string().min(1, 'Title is required'),
});
export const livePresentationUpdateSchema = withAtLeastOneField(z.object({
    attachmentId: positiveIdSchema.optional(),
    currentPage: z.number().int().positive().optional(),
    isActive: z.boolean().optional(),
    minutesEntryId: nullablePositiveIdSchema.optional(),
    orderById: z.number().int().min(1).optional(),
    pageCount: z.number().int().positive().nullable().optional(),
    title: z.string().min(1, 'Title is required').optional(),
}), ['attachmentId', 'currentPage', 'isActive', 'minutesEntryId', 'orderById', 'pageCount', 'title']);
export const displayTemplateCreateSchema = z.object({
    isDefault: z.boolean().default(false),
    layout: z.record(z.string(), z.unknown()),
    name: z.string().min(1, 'Template name is required'),
    type: liveMeetingDisplayModeSchema,
});
export const displayTemplateUpdateSchema = withAtLeastOneField(z.object({
    isDefault: z.boolean().optional(),
    layout: z.record(z.string(), z.unknown()).optional(),
    name: z.string().min(1, 'Template name is required').optional(),
    type: liveMeetingDisplayModeSchema.optional(),
}), ['isDefault', 'layout', 'name', 'type']);
const memberItemAnnotationTargetFields = [
    'attachmentId',
    'agendaSectionId',
    'agendaSectionItemId',
];
const hasAnnotationTarget = (data) => {
    const targetData = data;
    return memberItemAnnotationTargetFields.some((field) => targetData[field] != null);
};
const hasSingleAnnotationTarget = (data) => {
    const targetData = data;
    const targetCount = memberItemAnnotationTargetFields.reduce((count, field) => {
        return targetData[field] != null ? count + 1 : count;
    }, 0);
    return targetCount === 1;
};
export const memberItemAnnotationCreateSchema = z
    .object({
    agendaSectionId: nullablePositiveIdSchema.optional(),
    agendaSectionItemId: nullablePositiveIdSchema.optional(),
    annotationType: z.string().min(1, 'Annotation type is required'),
    attachmentId: nullablePositiveIdSchema.optional(),
    content: z.string().nullable().optional(),
    data: z.record(z.string(), z.unknown()),
    meetingMemberId: positiveIdSchema,
    pageNumber: z.number().int().positive().nullable().optional(),
})
    .refine(hasAnnotationTarget, {
    message: 'At least one annotation target is required',
})
    .refine(hasSingleAnnotationTarget, {
    message: 'Exactly one annotation target is required',
});
export const memberItemAnnotationUpdateSchema = withAtLeastOneField(z.object({
    agendaSectionId: nullablePositiveIdSchema.optional(),
    agendaSectionItemId: nullablePositiveIdSchema.optional(),
    annotationType: z.string().min(1, 'Annotation type is required').optional(),
    attachmentId: nullablePositiveIdSchema.optional(),
    content: z.string().nullable().optional(),
    data: z.record(z.string(), z.unknown()).optional(),
    pageNumber: z.number().int().positive().nullable().optional(),
}), [
    'agendaSectionId',
    'agendaSectionItemId',
    'annotationType',
    'attachmentId',
    'content',
    'data',
    'pageNumber',
]).refine((data) => {
    const targetData = data;
    const targetFieldsProvided = memberItemAnnotationTargetFields.some((field) => targetData[field] !== undefined);
    if (!targetFieldsProvided) {
        return true;
    }
    return hasSingleAnnotationTarget(data);
}, {
    message: 'Exactly one annotation target is required when updating annotation targets',
});
const eventEnvelopeSchema = z
    .object({
    clientEventId: z.uuid('clientEventId must be a valid UUID'),
    occurredAt: z.iso.datetime('occurredAt must be an ISO datetime'),
})
    .describe('Shared envelope for client-originated live meeting events. clientEventId provides idempotency.');
const itemActivatedEventSchema = eventEnvelopeSchema.extend({
    eventType: z.literal(LiveMeetingEventType.item_activated),
    payload: z.object({
        itemNumber: z.string().min(1).optional(),
        itemTitle: z.string().min(1, 'itemTitle is required'),
        minutesEntryId: positiveIdSchema,
        sectionTitle: z.string().min(1).optional(),
    }),
});
const motionCreatedEventSchema = eventEnvelopeSchema.extend({
    eventType: z.literal(LiveMeetingEventType.motion_created),
    payload: z.object({
        legislativeActionId: positiveIdSchema,
        minutesEntryId: nullablePositiveIdSchema.optional(),
        motionId: positiveIdSchema,
        // Nullable/relaxed so logging a freshly created motion never rejects valid data
        // (motions can have no mover yet and an empty or absent motion text).
        motionText: z.string().nullable().optional(),
        moverId: nullablePositiveIdSchema.optional(),
        seconderId: nullablePositiveIdSchema.optional(),
    }),
});
const voteOpenedEventSchema = eventEnvelopeSchema.extend({
    eventType: z.literal(LiveMeetingEventType.vote_opened),
    payload: z.object({
        motionId: positiveIdSchema,
    }),
});
const voteCastEventSchema = eventEnvelopeSchema.extend({
    eventType: z.literal(LiveMeetingEventType.vote_cast),
    payload: z.object({
        meetingMemberId: positiveIdSchema,
        motionId: positiveIdSchema,
        vote: voteTypeSchema,
    }),
});
const voteClosedEventSchema = eventEnvelopeSchema.extend({
    eventType: z.literal(LiveMeetingEventType.vote_closed),
    payload: z.object({
        motionId: positiveIdSchema,
    }),
});
const voteCancelledEventSchema = eventEnvelopeSchema.extend({
    eventType: z.literal(LiveMeetingEventType.vote_cancelled),
    payload: z.object({
        motionId: positiveIdSchema,
    }),
});
const speakerStartedEventSchema = eventEnvelopeSchema.extend({
    eventType: z.literal(LiveMeetingEventType.speaker_started),
    payload: z.object({
        minutesEntryId: nullablePositiveIdSchema.optional(),
        speakerId: positiveIdSchema,
        speakerName: z.string().min(1, 'speakerName is required'),
    }),
});
const speakerFinishedEventSchema = eventEnvelopeSchema.extend({
    eventType: z.literal(LiveMeetingEventType.speaker_finished),
    payload: z.object({
        minutesEntryId: nullablePositiveIdSchema.optional(),
        speakerId: positiveIdSchema,
        speakerName: z.string().min(1, 'speakerName is required'),
    }),
});
const speakerTimeAddedEventSchema = eventEnvelopeSchema.extend({
    eventType: z.literal(LiveMeetingEventType.speaker_time_added),
    payload: z.object({
        addedSeconds: z.number().int().positive(),
        speakerId: positiveIdSchema,
        speakerName: z.string().min(1, 'speakerName is required'),
    }),
});
const rollCallInitiatedEventSchema = eventEnvelopeSchema.extend({
    eventType: z.literal(LiveMeetingEventType.roll_call_initiated),
    payload: z.object({
        meetingBodyId: positiveIdSchema,
        rollCallId: positiveIdSchema,
    }),
});
const rollCallCompletedEventSchema = eventEnvelopeSchema.extend({
    eventType: z.literal(LiveMeetingEventType.roll_call_completed),
    payload: z.object({
        absentCount: nonNegativeIntegerSchema,
        presentCount: nonNegativeIntegerSchema,
        rollCallId: positiveIdSchema,
    }),
});
const consentItemPulledEventSchema = eventEnvelopeSchema.extend({
    eventType: z.literal(LiveMeetingEventType.consent_item_pulled),
    payload: z.object({
        consentSectionEntryId: positiveIdSchema,
        minutesEntryId: positiveIdSchema,
    }),
});
export const liveMeetingEventContractSchema = z
    .discriminatedUnion('eventType', [
    itemActivatedEventSchema,
    motionCreatedEventSchema,
    voteOpenedEventSchema,
    voteCastEventSchema,
    voteClosedEventSchema,
    voteCancelledEventSchema,
    speakerStartedEventSchema,
    speakerFinishedEventSchema,
    speakerTimeAddedEventSchema,
    rollCallInitiatedEventSchema,
    rollCallCompletedEventSchema,
    consentItemPulledEventSchema,
])
    .describe('Canonical event contract for live meeting real-time messages and meeting_event_logs payloads.');
export const meetingEventLogCreateSchema = z.object({
    actorId: nullablePositiveIdSchema.optional(),
    event: liveMeetingEventContractSchema,
    meetingEventId: positiveIdSchema,
});
export const liveEntryActivateSchema = z
    .object({
    clientEventId: z.uuid('clientEventId must be a valid UUID'),
    mediaArchiveId: nullablePositiveIdSchema.optional(),
    timestampSeconds: optionalTimestampSecondsSchema.nullable().optional(),
})
    .refine((data) => (data.mediaArchiveId == null && data.timestampSeconds == null) ||
    (data.mediaArchiveId != null && data.timestampSeconds != null), {
    message: 'mediaArchiveId and timestampSeconds must be provided together',
});
export const liveEntryRecommendedActionUpdateSchema = z
    .object({
    recommendedActionText: z.string().max(10_000).nullable(),
})
    .describe('Updates the recommended action text for the source item or section.');
